import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import toastr from 'toastr';
import { Button, Modal } from 'react-bootstrap';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { getPatientMedications } from '../../../../services/clinicPortalServices/patientMedicationService';
import { getUserRole, getUserDetails } from '../../../../services/common/util';
import EditBtnCellRenderer from './editBtnCellRenderer';
import StatusCellRenderer from './statusCellRenderer';
import PatientMedicationSearchMenu from './patientMedicationSearchMenu';
import PatientMedicationBreadcrumb from './patientMedicationBreadcrumb';
import AddPatientMedication from './addPatientMedication';
import { ModalStyled } from '../../../../theme/customizedStyleComponents';
import PatientMedicationDetails from './patientMedicationDetails';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class PatientMedicationGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            quickFilterText: '',
            paginationPageSize: 20,
            columnDefs: [
                {
                    headerName: 'Actions',
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'editBtnCellRenderer',
                    pinned: 'left'
                },
                {
                    headerName: 'Status',
                    field: 'status',
                    minWidth: 120,
                    cellRenderer: 'statusCellRenderer'
                },
                {
                    headerName: 'Patient',
                    field: 'patientId.full_name',
                    minWidth: 150,
                    valueGetter: (params) => {
                        if (params.data && params.data.patientId) {
                            const patient = params.data.patientId;
                            const firstName = patient.first_name || '';
                            const lastName = patient.last_name || '';
                            return `${firstName} ${lastName}`.trim() || 'Unknown Patient';
                        }
                        return 'Unknown Patient';
                    }
                },
                {
                    headerName: 'MRN',
                    field: 'patientId.mrn',
                    minWidth: 100
                },
                {
                    headerName: 'Medication',
                    field: 'medicationId.name',
                    minWidth: 200
                },
                {
                    headerName: 'Dosage',
                    field: 'dosageDisplay',
                    minWidth: 120,
                    valueGetter: (params) => {
                        return `${params.data.dosage} ${params.data.dosageUnit}`;
                    }
                },
                {
                    headerName: 'Frequency',
                    field: 'frequency',
                    minWidth: 120
                },
                {
                    headerName: 'Route',
                    field: 'route',
                    minWidth: 100
                },
                {
                    headerName: 'Start Date',
                    field: 'startDate',
                    minWidth: 120,
                    valueGetter: (params) => {
                        return params.data.startDate ? moment(params.data.startDate).format('MM/DD/YYYY') : '';
                    }
                },
                {
                    headerName: 'End Date',
                    field: 'endDate',
                    minWidth: 120,
                    valueGetter: (params) => {
                        return params.data.endDate ? moment(params.data.endDate).format('MM/DD/YYYY') : '';
                    }
                },
                {
                    headerName: 'Prescriber',
                    field: 'prescriber.name',
                    minWidth: 150
                },
                {
                    headerName: 'Last Dose',
                    field: 'lastDoseTime',
                    minWidth: 150,
                    valueGetter: (params) => {
                        return params.data.lastDoseTime
                            ? moment(params.data.lastDoseTime).format('MM/DD/YYYY HH:mm')
                            : '';
                    }
                },
                {
                    headerName: 'Verified',
                    field: 'isVerified',
                    minWidth: 80,
                    valueGetter: (params) => {
                        return params.data.isVerified ? 'Yes' : 'No';
                    }
                },
                {
                    headerName: 'Verified By',
                    field: 'verifiedBy.user_name',
                    minWidth: 120
                },
                {
                    headerName: 'Created Date',
                    field: 'createdAt',
                    minWidth: 150,
                    valueGetter: (params) => {
                        return params.data.createdAt ? moment(params.data.createdAt).format('MM/DD/YYYY HH:mm') : '';
                    }
                }
            ],
            components: {
                editBtnCellRenderer: EditBtnCellRenderer,
                statusCellRenderer: StatusCellRenderer
            },
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true,
                resizable: true,
                enableRowGroup: true,
                enablePivot: true,
                enableValue: true
            },
            rowData: [],
            context: { componentParent: this },
            showAddModal: false,
            showEditModal: false,
            selectedMedication: null,
            selectedPatientId: null,
            isAdminView: false,
            pageSize: 20,
            paginationNumberFormatter: function (params) {
                return '[' + params.value.toLocaleString() + ']';
            }
        };
    }

    componentDidMount() {
        // Check if we're in admin view or patient-specific view
        const patientId = this.props.match?.params?.patientId || this.props.patientId;
        const isAdminView = !patientId;

        this.setState({
            selectedPatientId: patientId,
            isAdminView
        });

        if (patientId) {
            this.loadPatientMedications(patientId);
        }
    }

    onGridReady = (params) => {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;

        // Auto-size columns to fit
        params.api.sizeColumnsToFit();
    };

    loadPatientMedications = (patientId) => {
        if (!patientId) {
            toastr.error('Patient ID is required');
            return;
        }

        getPatientMedications(patientId)
            .then((response) => {
                if (response.status === 200) {
                    this.setState({ rowData: response.data });
                } else {
                    toastr.error('Error loading patient medications');
                }
            })
            .catch((error) => {
                console.error('Error loading medications:', error);
                toastr.error('Error loading patient medications');
            });
    };

    refreshGrid = () => {
        if (this.state.selectedPatientId) {
            this.loadPatientMedications(this.state.selectedPatientId);
        }
    };

    clearFilter = () => {
        this.gridApi.setFilterModel(null);
        this.gridApi.onFilterChanged();
        this.setState({ quickFilterText: '' });
    };

    onBtnExport = () => {
        const fileName = `PatientMedications_${moment().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`;
        this.gridApi.exportDataAsExcel({
            fileName: fileName,
            sheetName: 'Patient Medications'
        });

        // Audit log for export
        const userDetails = JSON.parse(getUserDetails());
        console.log('Export triggered by:', userDetails.user_name);
    };

    onQuickFilterChanged = (value) => {
        this.setState({ quickFilterText: value });
    };

    onPageSizeChanged = (newPageSize) => {
        this.setState({ paginationPageSize: Number(newPageSize) });
    };

    handleAddMedication = () => {
        if (!this.state.selectedPatientId && !this.state.isAdminView) {
            toastr.error('Please select a patient first');
            return;
        }
        this.setState({ showAddModal: true });
    };

    handleCloseAddModal = () => {
        this.setState({ showAddModal: false });
    };

    handleEditMedication = (medication) => {
        this.setState({
            selectedMedication: medication,
            showEditModal: true
        });
    };

    handleCloseEditModal = () => {
        this.setState({
            selectedMedication: null,
            showEditModal: false
        });
    };

    updateGridRow = (nodeId, updatedData) => {
        const rowNode = this.gridApi.getRowNode(nodeId);
        if (rowNode) {
            rowNode.setData(updatedData);
        }
    };

    render() {
        const userRole = getUserRole();
        const canEdit = !/audit|salesperson/.test(userRole.toLowerCase());

        // Filter columns based on role
        let columnDefs = canEdit
            ? this.state.columnDefs
            : this.state.columnDefs.filter((x) => x.headerName !== 'Actions');

        return (
            <div className="clinic-contain">
                <PatientMedicationBreadcrumb
                    patientId={this.state.selectedPatientId}
                    isAdminView={this.state.isAdminView}
                />
                <div className="p-3">
                    <div className="row mb-3">
                        <div className="col-12">
                            <PatientMedicationSearchMenu
                                onQuickFilterChanged={this.onQuickFilterChanged}
                                onClearFilter={this.clearFilter}
                                onExport={this.onBtnExport}
                                onPageSizeChanged={this.onPageSizeChanged}
                                pageSize={this.state.pageSize}
                                quickFilterText={this.state.quickFilterText}
                                patientId={this.state.selectedPatientId}
                                onPatientSelected={(patientId) => {
                                    this.setState({ selectedPatientId: patientId });
                                    this.loadPatientMedications(patientId);
                                }}
                                onAddMedication={this.handleAddMedication}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <ThemeContext.Consumer>
                                {({ themeName }) => (
                                    <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            defaultColDef={this.state.defaultColDef}
                                            onGridReady={this.onGridReady}
                                            rowData={this.state.rowData}
                                            pagination={true}
                                            paginationPageSize={this.state.paginationPageSize}
                                            paginationNumberFormatter={this.state.paginationNumberFormatter}
                                            quickFilterText={this.state.quickFilterText}
                                            context={this.state.context}
                                            components={this.state.components}
                                            theme={themeName === 'Light' ? themeLight : themeDark}
                                            suppressRowClickSelection={true}
                                            rowSelection="single"
                                        />
                                    </div>
                                )}
                            </ThemeContext.Consumer>
                        </div>
                    </div>
                </div>

                {/* Add Medication Modal */}
                <AddPatientMedication
                    show={this.state.showAddModal}
                    handleClose={this.handleCloseAddModal}
                    patientId={this.state.selectedPatientId}
                    refreshGrid={this.refreshGrid}
                />

                {/* Edit Medication Modal */}
                {this.state.showEditModal && (
                    <ModalStyled
                        size="lg"
                        show={this.state.showEditModal}
                        onHide={this.handleCloseEditModal}
                        backdrop="static"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Patient Medication</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <PatientMedicationDetails
                                medication={this.state.selectedMedication}
                                handleClose={this.handleCloseEditModal}
                                refreshGrid={this.refreshGrid}
                                context={this.state.context}
                                hideFooter={true}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={this.handleCloseEditModal}>
                                Cancel
                            </Button>
                            {(this.state.selectedMedication?.status === 'active' || !this.state.selectedMedication?._id) && (
                                <Button 
                                    variant="primary" 
                                    onClick={() => {
                                        // Trigger form submission in the child component
                                        const form = document.querySelector('.medication-details-form');
                                        if (form) {
                                            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                                        }
                                    }}
                                >
                                    Update Medication
                                </Button>
                            )}
                        </Modal.Footer>
                    </ModalStyled>
                )}
            </div>
        );
    }
}
